import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "../api";
import { formatMoney } from "../lib/format";
import { useSettings } from "../SettingsContext";
import DocumentPreview from "./DocumentPreview";

const emptyDraft = () => ({
  invoice_number: "",
  invoice_date: "",
  due_date: "",
  amount: "",
  tax: "",
  notes: "",
  is_paid: false,
});

const InvoicesPanel = ({ account, onChange, refreshKey = 0 }) => {
  const { settings } = useSettings();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingDraft, setAddingDraft] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [extractingId, setExtractingId] = useState(null);
  const [uploadingNew, setUploadingNew] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState({}); // invoice_id -> [files]
  const [accountFiles, setAccountFiles] = useState([]); // all account files (for receipt picker)
  const [receiptPickerFor, setReceiptPickerFor] = useState(null); // invoice_id or null
  const [uploadingReceiptFor, setUploadingReceiptFor] = useState(null);
  const [attachingReceiptId, setAttachingReceiptId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft());
  const [savingEdit, setSavingEdit] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/accounts/${account.id}/invoices`);
      setInvoices(r.data);
      // fetch attached files per invoice in parallel
      const filesMap = {};
      await Promise.all(
        r.data.map(async (inv) => {
          try {
            const fr = await axios.get(`${API}/invoices/${inv.id}/files`);
            filesMap[inv.id] = fr.data;
          } catch {
            filesMap[inv.id] = [];
          }
        })
      );
      setInvoiceFiles(filesMap);
      // Also fetch all account-level files so the receipt picker can list
      // unassigned documents (files with no invoice_id and no statement_id).
      try {
        const afr = await axios.get(`${API}/accounts/${account.id}/files`);
        setAccountFiles(afr.data);
      } catch {
        setAccountFiles([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [account.id, refreshKey]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const saveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      const payload = {
        invoice_number: draft.invoice_number || null,
        invoice_date: draft.invoice_date || null,
        due_date: draft.due_date || null,
        amount: draft.amount === "" ? null : parseFloat(draft.amount),
        tax: draft.tax === "" ? null : parseFloat(draft.tax),
        notes: draft.notes || null,
        is_paid: !!draft.is_paid,
      };
      await axios.post(`${API}/accounts/${account.id}/invoices`, payload);
      setAddingDraft(false);
      setDraft(emptyDraft());
      await fetchInvoices();
      if (onChange) await onChange();
      toast.success("Invoice added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to add invoice");
    } finally {
      setSavingDraft(false);
    }
  };

  const updateInvoice = async (id, patch) => {
    try {
      await axios.put(`${API}/invoices/${id}`, patch);
      await fetchInvoices();
      if (onChange) await onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to update invoice");
    }
  };

  const startEdit = (inv) => {
    setEditingId(inv.id);
    setEditDraft({
      invoice_number: inv.invoice_number || "",
      invoice_date: inv.invoice_date || "",
      due_date: inv.due_date || "",
      amount: inv.amount == null ? "" : String(inv.amount),
      tax: inv.tax == null ? "" : String(inv.tax),
      notes: inv.notes || "",
      is_paid: !!inv.is_paid,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft());
  };

  const saveEdit = async (id) => {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      const payload = {
        invoice_number: editDraft.invoice_number || null,
        invoice_date: editDraft.invoice_date || null,
        due_date: editDraft.due_date || null,
        amount: editDraft.amount === "" ? null : parseFloat(editDraft.amount),
        tax: editDraft.tax === "" ? null : parseFloat(editDraft.tax),
        notes: editDraft.notes || null,
        is_paid: !!editDraft.is_paid,
      };
      await axios.put(`${API}/invoices/${id}`, payload);
      setEditingId(null);
      setEditDraft(emptyDraft());
      await fetchInvoices();
      if (onChange) await onChange();
      toast.success("Invoice updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to update invoice");
    } finally {
      setSavingEdit(false);
    }
  };

  const togglePaid = async (inv) => {
    await updateInvoice(inv.id, { is_paid: !inv.is_paid });
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number || "(untitled)"}?`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`);
      await fetchInvoices();
      if (onChange) await onChange();
      toast.success("Invoice deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to delete invoice");
    }
  };

  const uploadAndExtract = async (invoice, file) => {
    setUploadingId(invoice.id);
    try {
      const form = new FormData();
      form.append("files", file);
      form.append("invoice_id", invoice.id);
      const r = await axios.post(`${API}/accounts/${account.id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = r.data[0];
      setUploadingId(null);
      const uploadedType = uploaded ? uploaded.file_type || "" : "";
      if (uploaded && (uploadedType === "application/pdf" || uploadedType.startsWith("image/") || uploadedType.startsWith("text/"))) {
        setExtractingId(invoice.id);
        try {
          const ex = await axios.post(`${API}/invoices/${invoice.id}/extract`, { file_id: uploaded.id });
          toast.success("PDF attached — fields extracted");
          await fetchInvoices();
          if (onChange) await onChange();
          return ex.data;
        } catch (e) {
          toast.warning(`PDF attached, but extraction failed: ${formatApiErrorDetail(e) || "unknown"}`);
          await fetchInvoices();
        } finally {
          setExtractingId(null);
        }
      } else {
        toast.success("File attached");
        await fetchInvoices();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const onFileForInvoice = (invoice) => (e) => {
    const f = e.target.files[0];
    if (!f) return;
    uploadAndExtract(invoice, f);
    e.target.value = "";
  };

  const uploadReceipt = async (invoice, file) => {
    setUploadingReceiptFor(invoice.id);
    try {
      const form = new FormData();
      form.append("files", file);
      form.append("invoice_id", invoice.id);
      form.append("is_receipt", "true");
      await axios.post(`${API}/accounts/${account.id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceiptPickerFor(null);
      await fetchInvoices();
      if (onChange) await onChange();
      toast.success("Receipt uploaded — invoice marked paid");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to upload receipt");
    } finally {
      setUploadingReceiptFor(null);
    }
  };

  const attachExistingReceipt = async (invoice, file) => {
    setAttachingReceiptId(file.id);
    try {
      const fd = new FormData();
      fd.append("invoice_id", invoice.id);
      fd.append("is_receipt", "true");
      await axios.put(`${API}/files/${file.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceiptPickerFor(null);
      await fetchInvoices();
      if (onChange) await onChange();
      toast.success("Receipt attached — invoice marked paid");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to attach receipt");
    } finally {
      setAttachingReceiptId(null);
    }
  };

  const removeReceipt = async (file) => {
    if (!window.confirm(`Detach receipt "${file.original_filename}" from this invoice? The file will go back to Documents (unassigned).`)) return;
    try {
      const fd = new FormData();
      fd.append("invoice_id", "__clear__");
      fd.append("is_receipt", "false");
      await axios.put(`${API}/files/${file.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchInvoices();
      if (onChange) await onChange();
      toast.success("Receipt detached");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to detach receipt");
    }
  };

  const unassignedDocs = accountFiles.filter(
    (f) => !f.invoice_id && !f.statement_id && !f.is_receipt
  );

  const openFile = (file) => setPreviewFile(file);

  const createFromFile = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setUploadingNew(true);
    try {
      const r = await axios.post(`${API}/accounts/${account.id}/invoices`, {});
      await uploadAndExtract(r.data, f);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Failed to create invoice");
    } finally {
      setUploadingNew(false);
    }
  };

  const totalUnpaid = invoices
    .filter((i) => !i.is_paid)
    .reduce((s, i) => s + (i.amount || 0), 0);
  const totalTax = invoices.reduce((s, i) => s + (i.tax || 0), 0);

  return (
    <div data-testid="invoices-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">
          Invoices <span className="text-gray-400 font-normal">({invoices.length})</span>
        </h3>
        <div className="flex items-center gap-2">
          {totalUnpaid > 0 && (
            <span className="text-xs text-red-700 font-semibold" data-testid="invoices-unpaid-total">
              Unpaid: {formatMoney(totalUnpaid, settings)}
            </span>
          )}
          <label
            data-testid="invoices-upload-new"
            title="Upload a PDF or image — a new invoice is created and its fields auto-extracted"
            className={`px-3 py-1.5 text-xs font-medium border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 cursor-pointer ${
              uploadingNew ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            {uploadingNew ? "Uploading…" : "Upload PDF"}
            <input
              type="file"
              accept="application/pdf,image/*,.txt"
              className="hidden"
              onChange={createFromFile}
              disabled={uploadingNew}
            />
          </label>
          <button
            type="button"
            onClick={() => setAddingDraft((v) => !v)}
            data-testid="invoices-add-button"
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {addingDraft ? "Cancel" : "+ Add Invoice"}
          </button>
        </div>
      </div>

      {addingDraft && (
        <div className="border border-blue-200 bg-blue-50/50 rounded-md p-3 mb-3" data-testid="invoices-draft-row">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <input
              type="text"
              placeholder="Invoice #"
              value={draft.invoice_number}
              onChange={(e) => setDraft({ ...draft, invoice_number: e.target.value })}
              className="px-2 py-1.5 text-sm border rounded border-gray-300"
              data-testid="invoices-draft-number"
            />
            <input
              type="date"
              value={draft.invoice_date}
              onChange={(e) => setDraft({ ...draft, invoice_date: e.target.value })}
              className="px-2 py-1.5 text-sm border rounded border-gray-300"
              data-testid="invoices-draft-date"
            />
            <input
              type="date"
              value={draft.due_date}
              onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
              className="px-2 py-1.5 text-sm border rounded border-gray-300"
              data-testid="invoices-draft-due"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="Amount"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="px-2 py-1.5 text-sm border rounded border-gray-300"
              data-testid="invoices-draft-amount"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="Tax"
              value={draft.tax}
              onChange={(e) => setDraft({ ...draft, tax: e.target.value })}
              className="px-2 py-1.5 text-sm border rounded border-gray-300"
              data-testid="invoices-draft-tax"
            />
            <input
              type="text"
              placeholder="Notes"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              className="px-2 py-1.5 text-sm border rounded border-gray-300"
              data-testid="invoices-draft-notes"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={draft.is_paid}
                onChange={(e) => setDraft({ ...draft, is_paid: e.target.checked })}
                data-testid="invoices-draft-paid"
              />
              Paid
            </label>
            <button
              type="button"
              onClick={saveDraft}
              disabled={savingDraft}
              data-testid="invoices-draft-save"
              className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {savingDraft ? "Saving…" : "Save invoice"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-gray-500">Loading invoices…</div>
      ) : invoices.length === 0 && !addingDraft ? (
        <div className="py-8 text-center text-sm text-gray-500" data-testid="invoices-empty">
          No invoices yet. Add one to get started, or{" "}
          <label className="text-blue-600 hover:underline cursor-pointer font-medium" data-testid="invoices-empty-upload">
            upload a PDF
            <input
              type="file"
              accept="application/pdf,image/*,.txt"
              className="hidden"
              onChange={createFromFile}
              disabled={uploadingNew}
            />
          </label>{" "}
          to create an invoice with auto-extracted fields.
          {uploadingNew && <span className="ml-2 text-blue-600">Uploading…</span>}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Paid</th>
                <th className="text-left px-3 py-2">Invoice #</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-right px-3 py-2">Tax</th>
                <th className="text-left px-3 py-2">Notes</th>
                <th className="text-left px-3 py-2">PDF</th>
                <th className="text-left px-3 py-2">Receipt</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const attached = invoiceFiles[inv.id] || [];
                const invoiceDocs = attached.filter((f) => !f.is_receipt);
                const receipts = attached.filter((f) => f.is_receipt);
                const receipt = receipts[0];
                const isOverdue =
                  !inv.is_paid && inv.due_date && new Date(inv.due_date + "T00:00:00") < new Date(new Date().setHours(0, 0, 0, 0));
                const isEditing = editingId === inv.id;
                if (isEditing) {
                  return (
                    <tr
                      key={inv.id}
                      data-testid={`invoice-row-${inv.id}`}
                      className="border-t border-gray-100 bg-blue-50/60"
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={editDraft.is_paid}
                          onChange={(e) => setEditDraft({ ...editDraft, is_paid: e.target.checked })}
                          data-testid={`invoice-edit-paid-${inv.id}`}
                          className="h-4 w-4 accent-green-600"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={editDraft.invoice_number}
                          onChange={(e) => setEditDraft({ ...editDraft, invoice_number: e.target.value })}
                          placeholder="Invoice #"
                          className="w-full px-2 py-1 text-sm border rounded border-gray-300"
                          data-testid={`invoice-edit-number-${inv.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="date"
                          value={editDraft.invoice_date}
                          onChange={(e) => setEditDraft({ ...editDraft, invoice_date: e.target.value })}
                          className="w-full px-2 py-1 text-sm border rounded border-gray-300"
                          data-testid={`invoice-edit-date-${inv.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="date"
                          value={editDraft.due_date}
                          onChange={(e) => setEditDraft({ ...editDraft, due_date: e.target.value })}
                          className="w-full px-2 py-1 text-sm border rounded border-gray-300"
                          data-testid={`invoice-edit-due-${inv.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editDraft.amount}
                          onChange={(e) => setEditDraft({ ...editDraft, amount: e.target.value })}
                          placeholder="Amount"
                          className="w-full px-2 py-1 text-sm border rounded border-gray-300 text-right"
                          data-testid={`invoice-edit-amount-${inv.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editDraft.tax}
                          onChange={(e) => setEditDraft({ ...editDraft, tax: e.target.value })}
                          placeholder="Tax"
                          className="w-full px-2 py-1 text-sm border rounded border-gray-300 text-right"
                          data-testid={`invoice-edit-tax-${inv.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={editDraft.notes}
                          onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                          placeholder="Notes"
                          className="w-full px-2 py-1 text-sm border rounded border-gray-300"
                          data-testid={`invoice-edit-notes-${inv.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-xs">—</td>
                      <td className="px-3 py-2 text-gray-400 text-xs">—</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => saveEdit(inv.id)}
                          disabled={savingEdit}
                          data-testid={`invoice-save-${inv.id}`}
                          className="text-xs font-medium text-green-700 hover:text-green-900 mr-2 disabled:opacity-50"
                        >
                          {savingEdit ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          data-testid={`invoice-cancel-${inv.id}`}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    className={`border-t border-gray-100 hover:bg-blue-50/40 transition-colors ${inv.is_paid ? "bg-green-50/40" : ""}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={inv.is_paid}
                        onChange={() => togglePaid(inv)}
                        data-testid={`invoice-paid-${inv.id}`}
                        className="h-4 w-4 accent-green-600"
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{inv.invoice_number || "—"}</td>
                    <td className="px-3 py-2">{inv.invoice_date || "—"}</td>
                    <td className={`px-3 py-2 ${isOverdue ? "text-red-600 font-semibold" : ""}`} data-testid={`invoice-due-${inv.id}`}>
                      {inv.due_date || "—"}
                      {isOverdue && <span className="ml-1 text-xs">(overdue)</span>}
                    </td>
                    <td className="px-3 py-2 text-right" data-testid={`invoice-amount-${inv.id}`}>
                      {inv.amount != null ? formatMoney(inv.amount, settings) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {inv.tax != null ? formatMoney(inv.tax, settings) : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-[240px] truncate" title={inv.notes || ""}>
                      {inv.notes || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {invoiceDocs.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => openFile(invoiceDocs[0])}
                          className="text-xs text-blue-600 hover:underline text-left max-w-[180px] truncate"
                          title={invoiceDocs[0].original_filename}
                          data-testid={`invoice-file-${inv.id}`}
                        >
                          {invoiceDocs[0].original_filename}
                        </button>
                      ) : (
                        <label className="text-xs text-blue-600 hover:underline cursor-pointer" data-testid={`invoice-upload-${inv.id}`}>
                          {uploadingId === inv.id
                            ? "Uploading…"
                            : extractingId === inv.id
                            ? "Extracting…"
                            : "Upload PDF"}
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            className="hidden"
                            onChange={onFileForInvoice(inv)}
                            disabled={uploadingId === inv.id || extractingId === inv.id}
                          />
                        </label>
                      )}
                    </td>
                    <td className="px-3 py-2 relative">
                      {receipt ? (
                        <span className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openFile(receipt)}
                            className="text-xs text-emerald-700 hover:underline max-w-[140px] truncate text-left"
                            title={receipt.original_filename}
                            data-testid={`invoice-receipt-${inv.id}`}
                          >
                            {receipt.original_filename}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeReceipt(receipt)}
                            title="Detach receipt"
                            data-testid={`invoice-receipt-remove-${inv.id}`}
                            className="text-gray-400 hover:text-red-600 text-xs"
                          >
                            ✕
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setReceiptPickerFor(inv.id)}
                          data-testid={`invoice-receipt-add-${inv.id}`}
                          className="text-xs text-emerald-700 hover:underline"
                        >
                          + Receipt
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => startEdit(inv)}
                        data-testid={`invoice-edit-${inv.id}`}
                        className="text-xs text-blue-600 hover:text-blue-800 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteInvoice(inv)}
                        data-testid={`invoice-delete-${inv.id}`}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {invoices.length > 0 && (
              <tfoot className="bg-gray-50 text-xs">
                <tr className="border-t border-gray-200">
                  <td className="px-3 py-2 text-gray-500 uppercase" colSpan={4}>
                    Totals
                  </td>
                  <td className="px-3 py-2 text-right font-semibold" data-testid="invoices-total-amount">
                    {formatMoney(
                      invoices.reduce((s, i) => s + (i.amount || 0), 0),
                      settings
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700" data-testid="invoices-total-tax">
                    {formatMoney(totalTax, settings)}
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      <input ref={fileInputRef} className="hidden" type="file" />

      {previewFile && (
        <DocumentPreview file={previewFile} onClose={() => setPreviewFile(null)} />
      )}

      {receiptPickerFor && (() => {
        const inv = invoices.find((i) => i.id === receiptPickerFor);
        if (!inv) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            data-testid={`invoice-receipt-picker-${inv.id}`}
            onClick={() => setReceiptPickerFor(null)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-md p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  Attach receipt to invoice {inv.invoice_number ? `#${inv.invoice_number}` : ""}
                </h4>
                <button
                  type="button"
                  onClick={() => setReceiptPickerFor(null)}
                  data-testid={`invoice-receipt-picker-close-${inv.id}`}
                  className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Attaching a receipt marks this invoice as paid.
              </p>

              <label
                className={`block text-sm font-medium text-emerald-700 border border-emerald-300 rounded-md px-3 py-2 text-center cursor-pointer hover:bg-emerald-50 ${
                  uploadingReceiptFor === inv.id ? "opacity-50 pointer-events-none" : ""
                }`}
                data-testid={`invoice-receipt-upload-${inv.id}`}
              >
                {uploadingReceiptFor === inv.id ? "Uploading…" : "Upload new receipt"}
                <input
                  type="file"
                  accept="application/pdf,image/*,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    e.target.value = "";
                    if (f) uploadReceipt(inv, f);
                  }}
                  disabled={uploadingReceiptFor === inv.id}
                />
              </label>

              {unassignedDocs.length > 0 ? (
                <>
                  <div className="text-xs text-gray-500 mt-4 mb-1.5">
                    Or attach an existing document from this account:
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 border border-gray-100 rounded-md p-1">
                    {unassignedDocs.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => attachExistingReceipt(inv, doc)}
                        disabled={attachingReceiptId === doc.id}
                        data-testid={`invoice-receipt-existing-${inv.id}-${doc.id}`}
                        className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-emerald-50 disabled:opacity-50 truncate"
                        title={doc.original_filename}
                      >
                        {attachingReceiptId === doc.id ? "Attaching… " : ""}
                        {doc.original_filename}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-xs text-gray-400 italic mt-4 text-center">
                  No unassigned documents on this account to attach.
                </div>
              )}

              <button
                type="button"
                onClick={() => setReceiptPickerFor(null)}
                className="mt-4 w-full text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md py-1.5"
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default InvoicesPanel;
